/**
 * A resource as it is typed, and the rules that turn it into a request.
 *
 * Every rule here is the server's, the server applies them again, and where the
 * two disagree the server wins. Checking first saves a round trip — and, for
 * two of them, saves a person from a refusal that would arrive after the write
 * had already been declared:
 *
 * **`tcp`, `mcp`, `llm` and `web-egress` have no well-known port**, so for those
 * four a port is not optional. The other kinds fall back to the port of their
 * kind — `web` among them, at 80, because a browser handed `http://grafana.gds`
 * dials the scheme's port and an application whose row said nothing would be one
 * its own name never reaches.
 *
 * **A `web` application is asked for the domain it answers on**, and no other
 * kind is: gdsgate refuses `native_name` anywhere else. It is a whole name —
 * at least two labels — because it is one somebody else owns; a single label is
 * what `hostname` above is, and the two must not be typed into each other.
 *
 * What is **not** asked for anywhere is the connector backend's `url` or a way
 * out's `allow` list. Those are keys of the connector's own configuration file,
 * which lives in the customer's network and which the control plane does not
 * write; there is no field for either on the wire, so a box for them here would
 * collect text that goes nowhere.
 *
 * **A label with a comma or two spaces in a row is refused**, because the text
 * form gdsgate's catalogue takes does not escape either. That is a property of
 * the thing being written, not of this form, which is why the message says what
 * is wrong rather than quietly dropping the character.
 *
 * The form keeps everything as text — a half-typed port is not a number — and
 * `toResourceInput` is the one place that decides what a blank field means.
 */

import { RESOURCE_KINDS, isResourceKind } from '../../api/types';
import type { ClusterSession, Resource, ResourceInput, ResourceKind } from '../../api/types';
import type { SimpleKey } from '../../i18n/types';

export interface ResourceForm {
  /** The routing key, and the last segment of the address written to. */
  id: string;
  kind: ResourceKind;
  project: string;
  environment: string;
  /** Blank means the id. */
  hostname: string;
  /** Blank means the well-known port of the kind, which `tcp`, `mcp`, `llm` and
   * `web-egress` lack. */
  port: string;
  /** Space- or comma-separated. */
  aliases: string;
  parent: string;
  /** Blank means whatever the tenancy's policy asks for in general. */
  min_approvers: string;
  /** One `key=value` to a line. */
  labels: string;
  /**
   * Pin this MCP server's tool descriptors. Only an `mcp` resource serves
   * them, so the field is shown for that kind alone and sent for no other —
   * the server refuses a pin anywhere else rather than storing a promise it
   * cannot keep.
   */
  pin_tool_descriptors: boolean;
  /**
   * The domain a `web` application answers on. Shown for that kind alone and
   * sent for no other — the server refuses it elsewhere rather than storing a
   * name nothing resolves.
   */
  native_name: string;
}

export type ResourceField = keyof ResourceForm;
export type ResourceErrors = Partial<Record<ResourceField, SimpleKey>>;

/**
 * The kinds that have to be told which port to dial.
 *
 * `web-egress` is here and `web` is not, and the asymmetry is the lane's. An
 * application is opened by name in a browser, which dials the scheme's port; a
 * way out is never dialled by name at all — it is written into `$HTTP_PROXY`
 * with its port, and 3128, 8080 and 8888 are one product's habit each rather
 * than a port of the protocol.
 */
const PORTLESS_KINDS: readonly ResourceKind[] = ['tcp', 'mcp', 'llm', 'web-egress'];

/** The server's own defaults, so a fresh form shows what will be stored. */
export function emptyResourceForm(): ResourceForm {
  return {
    id: '',
    kind: 'ssh',
    project: 'default',
    environment: 'dev',
    hostname: '',
    port: '',
    aliases: '',
    parent: '',
    min_approvers: '',
    labels: '',
    pin_tool_descriptors: false,
    native_name: '',
  };
}

/**
 * A fresh form carrying what a connector offered.
 *
 * Only the two values the connector stated: the id it serves the resource
 * under, and the kind it serves it as. Everything else is a decision nobody has
 * made yet — the project, the environment, who may reach it — and pre-filling
 * those from a node's word would be letting the node make them.
 *
 * A kind this console does not know (or the `unknown` gdsgate prints when the
 * declaration carried none) leaves the field at its default, showing what will
 * actually be sent rather than a guess.
 */
export function formForOffer(offer: { resource_id: string; kind: string }): ResourceForm {
  const empty = emptyResourceForm();
  return {
    ...empty,
    id: offer.resource_id,
    kind: isResourceKind(offer.kind) ? offer.kind : empty.kind,
  };
}

/** An existing row, ready to be rewritten whole — which is what a write does. */
export function formForResource(resource: Resource): ResourceForm {
  const empty = emptyResourceForm();
  return {
    id: resource.id ?? '',
    // A kind this console does not know cannot be put in a closed list of
    // choices, and picking the nearest one would rewrite the resource into
    // something else. The form falls back and the field shows what it will send.
    kind: isResourceKind(resource.kind) ? resource.kind : empty.kind,
    project: resource.project,
    environment: resource.environment,
    hostname: resource.hostname ?? '',
    port: resource.port === undefined ? '' : String(resource.port),
    aliases: (resource.aliases ?? []).join(' '),
    parent: resource.parent ?? '',
    min_approvers: resource.min_approvers === undefined ? '' : String(resource.min_approvers),
    labels: Object.entries(resource.labels)
      .map(([key, value]) => `${key}=${value}`)
      .join('\n'),
    pin_tool_descriptors: resource.pin_tool_descriptors ?? false,
    native_name: resource.native_name ?? '',
  };
}

/** Whether a pin means anything for this kind. Only an MCP server serves tool
 * descriptors. */
export function servesToolDescriptors(kind: ResourceKind): boolean {
  return kind === 'mcp';
}

/**
 * Whether a domain of the application's own means anything for this kind.
 *
 * Only `web`. A way out is not one application and has no name to answer as;
 * every other kind carries bytes, and a name an application answers on is a
 * statement about HTTP.
 */
export function answersOnItsOwnDomain(kind: ResourceKind): boolean {
  return kind === 'web';
}

/** Whether this kind has to be given a port. */
export function needsPort(kind: ResourceKind): boolean {
  return PORTLESS_KINDS.includes(kind);
}

/**
 * The kinds to offer in this tenancy's console.
 *
 * The server's answer, not this module's list, because the answer is per
 * tenancy: a fleet moves in waves, and a kind that arrived in a release after
 * this tenancy's build is a word its cluster has never heard. Offering it would
 * be laying out a refusal.
 *
 * Two fallbacks, and they are the same fallback: a server that says nothing
 * (one from before the field existed) and a server whose whole answer is kinds
 * this console has no word for both leave it where it was — offering everything
 * it knows, and letting the tenancy's own cluster be the one that refuses. The
 * alternative in either case is a select with nothing in it, which stops a
 * person declaring anything at all.
 */
export function kindsOffered(session: ClusterSession | null): readonly ResourceKind[] {
  const offered = session?.resource_kinds;
  if (offered === undefined) return RESOURCE_KINDS;
  const known = offered.filter(isResourceKind);
  return known.length === 0 ? RESOURCE_KINDS : known;
}

const WHITESPACE = /\s/;
const DIGITS = /^\d+$/;

export function checkResource(form: ResourceForm): ResourceErrors {
  const errors: ResourceErrors = {};

  const id = form.id.trim();
  if (id === '') errors.id = 'resource.check.idRequired';
  else if (WHITESPACE.test(id)) errors.id = 'resource.check.idWhitespace';

  if (form.project.trim() === '') errors.project = 'resource.check.projectRequired';
  else if (WHITESPACE.test(form.project.trim())) errors.project = 'resource.check.noWhitespace';

  if (form.environment.trim() === '') errors.environment = 'resource.check.environmentRequired';
  else if (WHITESPACE.test(form.environment.trim()))
    errors.environment = 'resource.check.noWhitespace';

  if (WHITESPACE.test(form.hostname.trim())) errors.hostname = 'resource.check.noWhitespace';
  if (WHITESPACE.test(form.parent.trim())) errors.parent = 'resource.check.noWhitespace';

  const port = form.port.trim();
  if (port === '') {
    if (needsPort(form.kind)) errors.port = 'resource.check.portRequired';
  } else if (!DIGITS.test(port) || Number(port) < 1 || Number(port) > 65535) {
    errors.port = 'resource.check.portRange';
  }

  const approvers = form.min_approvers.trim();
  if (approvers !== '' && !DIGITS.test(approvers)) {
    errors.min_approvers = 'resource.check.approversNumber';
  }

  const native = form.native_name.trim();
  if (native !== '' && answersOnItsOwnDomain(form.kind) && !isWholeDomain(native)) {
    errors.native_name = 'resource.check.nativeName';
  }

  const labels = parseLabels(form.labels);
  if (!labels.ok) errors.labels = labels.reason;

  return errors;
}

/** One DNS label: letters, digits and `-`, not starting or ending with `-`. */
const LABEL = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/**
 * Whether this is a whole domain somebody else owns, which is the only shape a
 * `native_name` can have.
 *
 * At least two labels, and that is the load-bearing rule rather than a
 * formality: one label is a name in this cluster's own zone — what `hostname`
 * holds — and a value that could be read as either is a declaration two systems
 * would resolve differently. Case is folded rather than refused, because the
 * server folds it too and a person typing their own domain in capitals has made
 * no mistake.
 */
function isWholeDomain(name: string): boolean {
  const labels = name.replace(/\.$/, '').toLowerCase().split('.');
  return labels.length >= 2 && labels.every((label) => LABEL.test(label)) && name.length <= 253;
}

export type ParsedLabels =
  | { ok: true; labels: Record<string, string> }
  | { ok: false; reason: SimpleKey };

/**
 * `key=value` to a line, and the two characters gdsgate's catalogue cannot
 * carry are refused rather than trimmed away: a label that arrived as something
 * else is a Cedar tag that reads as something else.
 */
export function parseLabels(text: string): ParsedLabels {
  const labels: Record<string, string> = {};

  for (const line of text.split('\n')) {
    const entry = line.trim();
    if (entry === '') continue;

    const at = entry.indexOf('=');
    if (at <= 0) return { ok: false, reason: 'resource.check.labelShape' };

    const key = entry.slice(0, at).trim();
    const value = entry.slice(at + 1).trim();
    if (key === '' || value === '') return { ok: false, reason: 'resource.check.labelShape' };
    if (`${key}${value}`.includes(',')) return { ok: false, reason: 'resource.check.labelComma' };
    if (key.includes('  ') || value.includes('  ')) {
      return { ok: false, reason: 'resource.check.labelSpaces' };
    }

    labels[key] = value;
  }

  return { ok: true, labels };
}

/** Names, however they were separated. Empty entries are not names. */
export function parseAliases(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((alias) => alias.trim())
    .filter((alias) => alias !== '');
}

/**
 * The form as the server takes it — the whole resource, since a write replaces
 * rather than merges. A field left blank is left out entirely rather than sent
 * as an empty string: absent means "the default for this", and an empty string
 * would mean "this, and it is empty".
 */
export function toResourceInput(form: ResourceForm): ResourceInput {
  const labels = parseLabels(form.labels);
  const aliases = parseAliases(form.aliases);
  const hostname = form.hostname.trim();
  const parent = form.parent.trim();
  const port = form.port.trim();
  const approvers = form.min_approvers.trim();

  return {
    kind: form.kind,
    project: form.project.trim(),
    environment: form.environment.trim(),
    labels: labels.ok ? labels.labels : {},
    ...(parent === '' ? {} : { parent }),
    ...(approvers === '' ? {} : { min_approvers: Number(approvers) }),
    ...(hostname === '' ? {} : { hostname }),
    ...(aliases.length === 0 ? {} : { aliases }),
    ...(port === '' ? {} : { port: Number(port) }),
    // Sent only where it means something. A pin on any other kind is refused by
    // the server, and a form that sent `false` everywhere would be asking it to
    // decide a question that kind does not have.
    ...(servesToolDescriptors(form.kind) && form.pin_tool_descriptors
      ? { pin_tool_descriptors: true }
      : {}),
    // Same rule as the pin: sent only where it means something. Folded on the
    // way out because the server stores it folded, and a body that asked for
    // one spelling of a name the cluster holds in another would be rewritten on
    // every application for ever.
    ...(answersOnItsOwnDomain(form.kind) && form.native_name.trim() !== ''
      ? { native_name: form.native_name.trim().replace(/\.$/, '').toLowerCase() }
      : {}),
  };
}
